import React from 'react';
import { Link } from 'react-router-dom';
import { ArrowRightIcon, CalendarPlusIcon, CheckIcon, ClockIcon, CreditCardIcon, MapPinIcon, SearchIcon, SparklesIcon, UsersIcon } from 'lucide-react';
import { inscriptionCategories, inscriptionChild, type ActivitySlot } from '../../data/loisirs';
import type { InscriptionKind } from '../../hooks/useInscriptionConversation';
interface InscriptionMessageProps {
  kind: InscriptionKind;
  live: boolean;
  category: string | null;
  slots: ActivitySlot[];
  slot: ActivitySlot | null;
  onPickCategory: (category: string) => void;
  onRegister: (slot: ActivitySlot) => void;
  onRestart: () => void;
}
const eyebrow = 'flex items-center gap-2 text-sm font-bold text-ville-indigo';
const chipButton = 'rounded-full border border-slate-200 bg-white px-4 py-2.5 text-sm font-semibold text-ville-navy transition-colors duration-150 ease-ville hover:border-ville-indigo hover:bg-ville-indigo-soft hover:text-ville-indigo focus:outline-none focus-visible:ring-2 focus-visible:ring-ville-indigo';
function SlotDetails({
  slot
}: {
  slot: ActivitySlot;
}) {
  return <dl className="mt-2 space-y-1 text-[13px] text-slate-600" data-id="element-1552" data-mp="13ujcjmfddhdk">
      <div className="flex items-center gap-1.5" data-id="element-1553" data-mp="yuetppqzoak6">
        <ClockIcon className="h-3.5 w-3.5 shrink-0 text-slate-400" aria-hidden="true" data-id="element-1554" data-mp="yt3v04cehszx" />
        <dt className="sr-only" data-id="element-1555" data-mp="tu91o8gt4m1k">Horaire</dt>
        <dd data-id="element-1556" data-mp="txoet5qx7l0c">
          {slot.day} · {slot.time}
        </dd>
      </div>
      <div className="flex items-center gap-1.5" data-id="element-1557" data-mp="oxsaf5eb9rk9">
        <MapPinIcon className="h-3.5 w-3.5 shrink-0 text-slate-400" aria-hidden="true" data-id="element-1558" data-mp="k5r03p0peawv" />
        <dt className="sr-only" data-id="element-1559" data-mp="g3x8054kqf8b">Lieu</dt>
        <dd data-id="element-1560" data-mp="g4p1naem22vy">{slot.place}</dd>
      </div>
      <div className="flex items-center gap-1.5" data-id="element-1561" data-mp="g1jc5u22kyzp">
        <UsersIcon className="h-3.5 w-3.5 shrink-0 text-slate-400" aria-hidden="true" data-id="element-1562" data-mp="b5vnrgvew9v9" />
        <dt className="sr-only" data-id="element-1563" data-mp="65zh19vby4c0">Âge et places</dt>
        <dd data-id="element-1564" data-mp="67u34ric82ho">
          {slot.audience} · {slot.spots}
        </dd>
      </div>
    </dl>;
}
export function InscriptionMessage({
  kind,
  live,
  category,
  slots,
  slot,
  onPickCategory,
  onRegister,
  onRestart
}: InscriptionMessageProps) {
  if (kind === 'activite') {
    return <div data-id="element-1565" data-mp="3sot0m4nrpaqe">
        <p className={eyebrow} data-id="element-1566" data-mp="3sl59fc5sp0eu">
          <SparklesIcon className="h-4 w-4" aria-hidden="true" data-id="element-1567" data-mp="3eo8ht8hureww" />
          Inscription
        </p>
        <p className="mt-2 text-[17px] font-bold text-ville-navy" data-id="element-1568" data-mp="3ioshojtt8wmq">Quelle activité voulez-vous ?</p>
        <p className="mt-1 text-sm text-slate-500" data-id="element-1569" data-mp="a90fg4050t1r">
          Je cherche les places libres pour {inscriptionChild.name} ({inscriptionChild.age}).
        </p>
        {live && <ul className="mt-4 flex flex-wrap gap-2" data-id="element-1570" data-mp="1h6s9yq3sk37">
            {inscriptionCategories.map(option => <li key={option} data-id="element-1571" data-mp="5ik2el08b9fx">
                <button type="button" onClick={() => onPickCategory(option)} className={chipButton} data-id="element-1572" data-mp="5hqsjb5z43tk">
                  {option}
                </button>
              </li>)}
          </ul>}
      </div>;
  }
  if (kind === 'resultats') {
    const [best, ...others] = slots;
    if (!best) {
      return <div data-id="element-1573" data-mp="2inf912z2wrfw">
          <p className={eyebrow} data-id="element-1574" data-mp="2in5j1fe1aka7">
            <SearchIcon className="h-4 w-4" aria-hidden="true" data-id="element-1575" data-mp="2dr7047bh5uh6" />
            Recherche
          </p>
          <p className="mt-2 text-[17px] font-bold text-ville-navy" data-id="element-1576" data-mp="2sfl1edt8tavm">
            Aucune place trouvée pour cette activité.
          </p>
          <p className="mt-1 text-sm text-slate-500" data-id="element-1577" data-mp="2nh0a53vyy1jb">Nommez une autre activité et je regarde de nouveau.</p>
        </div>;
    }
    return <div data-id="element-1578" data-mp="1us2sn2zl08rm">
        <p className={eyebrow} data-id="element-1579" data-mp="1usclqzfxaw1j">
          <SearchIcon className="h-4 w-4" aria-hidden="true" data-id="element-1580" data-mp="1ut47lijpxfem" />
          {slots.length} places trouvées en {category}
        </p>
        <p className="mt-2 text-[17px] font-bold text-ville-navy" data-id="element-1581" data-mp="29mh66o0dmpyw">
          Voici ce que je recommande pour {inscriptionChild.name}
        </p>

        <article className="mt-4 rounded-2xl border-2 border-ville-navy bg-ville-sand p-4" data-id="element-1582" data-mp="3l2xocp3bfkpg">
          <div className="flex flex-wrap items-center gap-2" data-id="element-1583" data-mp="3g5p5sne99eno">
            <span className="rounded-full bg-ville-lime px-2.5 py-0.5 text-[11px] font-bold uppercase tracking-wide text-ville-navy" data-id="element-1584" data-mp="1pns8n9w4cgi1">
              Recommandé
            </span>
            <p className="text-[15px] font-extrabold text-ville-navy" data-id="element-1585" data-mp="1uozgf8y9hfop">{best.name}</p>
          </div>
          <SlotDetails slot={best} data-id="element-1586" data-mp="1yqkzxd5aws7v" />
          {best.note && <p className="mt-2 text-[13px] font-semibold text-ville-deep" data-id="element-1587" data-mp="23ntj9h26r2ap">{best.note}</p>}
          <p className="mt-2 text-[13px] font-bold text-ville-navy" data-id="element-1588" data-mp="28n67o68pghfe">{best.price}</p>
          {live && <button type="button" onClick={() => onRegister(best)} className="mt-4 inline-flex w-full items-center justify-center gap-2 rounded-full bg-ville-navy px-6 py-3 text-sm font-bold text-white transition-transform duration-150 ease-ville hover:-translate-y-0.5 focus:outline-none focus-visible:ring-2 focus-visible:ring-ville-indigo focus-visible:ring-offset-2 sm:w-auto" data-id="element-1589" data-mp="2dkxxmupshtgy">
              <CheckIcon className="h-4 w-4" aria-hidden="true" data-id="element-1590" data-mp="2wcicyv0ww5cf" />
              Inscrire {inscriptionChild.name} — 1 clic
            </button>}
        </article>

        {others.length > 0 && <ul className="mt-3 space-y-2" data-id="element-1591" data-mp="31frjhkwlof7g">
            {others.map(option => <li key={option.id} data-id="element-1592" data-mp="2wihx6e4icjnu">
                <div className="flex flex-wrap items-center justify-between gap-3 rounded-2xl border border-slate-200 px-4 py-3" data-id="element-1593" data-mp="2wjsxu52npbaq">
                  <div className="min-w-0" data-id="element-1594" data-mp="3p6wy5wqt11g6">
                    <p className="text-sm font-bold text-ville-navy" data-id="element-1595" data-mp="3p5vjzjehryu8">
                      {option.day} · {option.time}
                    </p>
                    <p className="truncate text-xs text-slate-500" data-id="element-1596" data-mp="3fgc1wfyls90f">
                      {option.place} · {option.spots}
                    </p>
                  </div>
                  {live && <button type="button" onClick={() => onRegister(option)} className={chipButton} data-id="element-1597" data-mp="bsaf2kiq5jdq">
                      Choisir
                    </button>}
                </div>
              </li>)}
          </ul>}
      </div>;
  }
  return <div data-id="element-1598" data-mp="267plyhdprcyc">
      <span className="grid h-11 w-11 place-items-center rounded-full bg-ville-mist text-ville-deep" aria-hidden="true" data-id="element-1599" data-mp="268ikww6fky5x">
        <CheckIcon className="h-6 w-6" data-id="element-1600" data-mp="1rijlyr71xipe" />
      </span>
      <h3 className="mt-3 text-xl font-extrabold tracking-tight text-ville-navy" data-id="element-1601" data-mp="1whnwbl5ppmhb">
        {inscriptionChild.name} est inscrite
      </h3>
      {slot && <>
          <p className="mt-1 text-[15px] font-bold text-ville-navy" data-id="element-1602" data-mp="1ikr8ztosyn56">{slot.name}</p>
          <SlotDetails slot={slot} data-id="element-1603" data-mp="1niss2em8amgn" />
          <p className="mt-3 flex items-center gap-1.5 text-[13px] text-slate-600" data-id="element-1604" data-mp="18kgv06uy1boy">
            <CreditCardIcon className="h-4 w-4 shrink-0 text-slate-400" aria-hidden="true" data-id="element-1605" data-mp="1djbmdx6z88ko" />
            {slot.price} · payé avec la carte enregistrée
          </p>
          <p className="mt-1 text-xs text-slate-500" data-id="element-1606" data-mp="32yriaavw3xsm">
            Confirmation envoyée par courriel · place réservée avec la carte citoyenne de la famille
          </p>
        </>}
      {live && <div className="mt-5 flex flex-wrap items-center gap-3" data-id="element-1607" data-mp="wa92l6a2e0sy">
          <Link to="/mes-loisirs" className="inline-flex items-center gap-2 rounded-full bg-ville-navy px-6 py-2.5 text-sm font-bold text-white transition-transform duration-150 ease-ville hover:-translate-y-0.5" data-id="element-1608" data-mp="rbo3592mzl5k">
            Voir mes activités
            <ArrowRightIcon className="h-4 w-4" aria-hidden="true" data-id="element-1609" data-mp="1ezxt8n6vuv2i" />
          </Link>
          <button type="button" className={`inline-flex items-center gap-2 ${chipButton}`} data-id="element-1610" data-mp="1ayvrnzphu6xd">
            <CalendarPlusIcon className="h-4 w-4" aria-hidden="true" data-id="element-1611" data-mp="1oyn0lm15kx4i" />
            Ajouter au calendrier
          </button>
          <button type="button" onClick={onRestart} className={chipButton} data-id="element-1612" data-mp="1jz23lh5jmyuf">
            Inscrire à autre chose
          </button>
        </div>}
    </div>;
}